import { getSubjectLabels } from '@/lib/settings';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import WizardShell from '@/components/ui/wizard-shell';
import { WizardCard, WizardKVGrid, WizardKVItem, WizardSection } from '@/components/ui/wizard-layout';
import CommunicationComposer, { type GroupDraftState, type RecipientGroup } from '@/components/communications/CommunicationComposer';
import StepLessonSchedule from '@/components/trials/trialWizard/StepLessonSchedule';
import StepRoomSelection from '@/components/trials/trialWizard/StepRoomSelection';
import StepDateSelection from '@/components/trials/trialWizard/StepDateSelection';
import type { DatePageModel, ScheduleView, SelectedSlot, Step1Section } from '@/components/trials/trialWizard/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { masterdataApi, type FachItem } from '@/lib/courses';
import { lehrerApi, unterrichtseinheitenApi, type LehrerOption } from '@/lib/unterrichtseinheiten';
import { getRooms, getRoomsUnits, type Room, type RoomUnit } from '@/lib/rooms';
import { personenApi, type TeacherAvailabilitySlot } from '@/lib/personen';
import { fetchTrials } from '@/lib/trials';
import { billingApi, type EnrollmentBillingOptions } from '@/lib/api/billing';
import { CONTINGENT_MODE_LABEL, tariffPriceLabel } from '@/lib/billing';
import { formatDate, toApiDate } from '@/lib/dateFormat';
import { api, historyTemplatesApi, migrationApi, type MigrationCaseDetailsResponse, type MigrationContractAdjustmentPayload } from '@/lib/api';
import { resolveLegacyFachName } from '@/lib/migrationCaseWizard';
import PersonAssignmentCard from './PersonAssignmentCard';
import MigrationPackageList from './MigrationPackageList';

type ActionType = 'contract_adjustment' | 'contract_termination' | 'order_adjustment';
type AssignmentMode = 'auto' | 'manual' | 'create_new';
type PersonRole = 'student' | 'contract_partner';

type Props = {
  open: boolean;
  caseId: number | null;
  actionType: ActionType;
  onOpenChange: (open: boolean) => void;
  loadCaseDetails: (caseId: number) => Promise<MigrationCaseDetailsResponse>;
  onPreview: (caseId: number, payload: Record<string, unknown>) => Promise<{ warnings?: string[] }>;
  onExecute: (caseId: number, payload: Record<string, unknown>) => Promise<{ ok: boolean }>;
  onDone?: () => Promise<void> | void;
};

interface AccountingPackage {
  id: string;
  name: string;
  units: number;
  discountPercent?: number;
  installmentMonths: number | null;
  features?: string[];
  highlighted?: boolean;
  highlightLabel?: string | null;
  special?: boolean;
  immediatePriceMode?: 'total' | 'per_unit';
  immediatePriceCents?: number;
  installmentPriceMode?: 'total' | 'per_unit';
  installmentPriceCents?: number;
  totalCentsImmediate?: number;
  pricePerUnitCentsImmediate?: number;
  totalCentsInstallment?: number;
  pricePerUnitCentsInstallment?: number;
  immediateDiscountPercent?: number;
  installmentEnabled?: boolean;
  defaultPaymentType?: 'immediate' | 'installment';
  sortOrder?: number;
}

interface PackagePrice {
  package: AccountingPackage;
  pricePerUnitCents: number;
  originalPricePerUnitCents: number;
  totalCents: number;
  originalTotalCents: number;
  currency: string;
}

type ContractAdjustmentWizardDraft = {
  action_type: 'contract_adjustment';
  total_steps: number;
  current_step: number;
  completed_steps: number[];
  wp_user_id: number;
  step1_active_section: Step1Section;
  schedule_view: ScheduleView;
  timetable_day_of_week: number;
  start_date_manually_selected: boolean;
  draft_payload: MigrationContractAdjustmentPayload;
};

const addMinutes = (hhmm: string, minutes: number): string => {
  const [h, m] = hhmm.split(':').map(Number);
  const base = new Date(1970, 0, 1, h || 0, m || 0, 0);
  base.setMinutes(base.getMinutes() + minutes);
  return `${String(base.getHours()).padStart(2, '0')}:${String(base.getMinutes()).padStart(2, '0')}`;
};

const overlaps = (startA: string, endA: string, startB: string, endB: string): boolean => startA < endB && startB < endA;

const addDaysYmd = (ymd: string, days: number): string => {
  const d = new Date(`${ymd}T00:00:00`);
  d.setDate(d.getDate() + days);
  return toApiDate(d);
};

const firstOfNextMonth = (): string => {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  d.setDate(1);
  return toApiDate(d);
};

const firstMatchingWeekdayInNextMonth = (dayOfWeek: number): string => {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  d.setDate(1);
  while (((d.getDay() + 6) % 7) + 1 !== dayOfWeek) {
    d.setDate(d.getDate() + 1);
  }
  return toApiDate(d);
};

const calculateAge = (birthDate: string): number | null => {
  const value = birthDate.trim();
  if (!value) return null;
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - date.getFullYear();
  const monthDiff = today.getMonth() - date.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate())) age -= 1;
  return age >= 0 ? age : null;
};

const formatAgeYearsMonths = (birthDate: string): string => {
  const value = birthDate.trim();
  if (!value) return '';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return '';
  const today = new Date();
  let years = today.getFullYear() - date.getFullYear();
  let months = today.getMonth() - date.getMonth();
  if (today.getDate() < date.getDate()) months -= 1;
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  if (years < 0) return '';
  const yearLabel = years === 1 ? '1 Jahr' : `${years} Jahre`;
  const monthLabel = months === 1 ? '1 Monat' : `${months} Monate`;
  return `${yearLabel}, ${monthLabel}`;
};

const normalizeCompare = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[()]/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\bmin\.?\b/g, 'min')
    .replace(/minuten?/g, 'min')
    .trim();

const resolveUnterrichtsformId = (
  legacyUformId: number,
  legacyUformName: string,
  resolvedUformId: number,
  options: Array<{ id: number; name: string }>,
): number => {
  if (resolvedUformId > 0 && options.some((u) => u.id === resolvedUformId)) {
    return resolvedUformId;
  }
  if (legacyUformId > 0) {
    const byLegacyId = options.find((u) => u.id === legacyUformId);
    if (byLegacyId) return byLegacyId.id;
  }
  return matchUnterrichtsformIdByName(legacyUformName, options);
};

const matchUnterrichtsformIdByName = (
  name: string,
  options: Array<{ id: number; name: string }>,
): number => {
  const needle = normalizeCompare(name);
  if (!needle) return 0;
  const exact = options.find((u) => normalizeCompare(u.name) === needle);
  if (exact) return exact.id;
  const includes = options.find((u) => {
    const hay = normalizeCompare(u.name);
    return hay.includes(needle) || needle.includes(hay);
  });
  return includes?.id ?? 0;
};

const computeDatePageModel = (
  selectedSlot: SelectedSlot,
  anchorDate: string,
  page: number,
  perPage: number,
): DatePageModel => {
  if (!selectedSlot) return null;
  const today = toApiDate(new Date());
  // Range bewusst breit: 56 Tage zurück + 168 Tage vor. Bei perPage=8 und
  // einem wöchentlichen Slot ergibt das genug Termine, damit immer mindestens
  // eine Seite in die Vergangenheit UND in die Zukunft blätterbar ist
  // (vorher: -28/+56 → alles passte auf eine einzige Seite, kein Blättern).
  const start = new Date(`${addDaysYmd(today, -56)}T00:00:00`);
  const end = new Date(`${addDaysYmd(today, 168)}T00:00:00`);

  const all: string[] = [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const ymd = toApiDate(d);
    const dow = ((d.getDay() + 6) % 7) + 1;
    if (dow === selectedSlot.dayOfWeek) all.push(ymd);
  }

  const anchor = anchorDate || firstOfNextMonth();
  const anchorIdxRaw = all.findIndex((d) => d >= anchor);
  const anchorIdx = Math.max(0, anchorIdxRaw);
  const minPage = -Math.floor(anchorIdx / perPage);
  const maxPage = Math.max(minPage, Math.ceil((all.length - anchorIdx) / perPage) - 1);
  const effectivePage = Math.min(maxPage, Math.max(minPage, page));
  const startIdx = anchorIdx + effectivePage * perPage;
  const pageDates = all.slice(Math.max(0, startIdx), Math.max(0, startIdx) + perPage);
  const pageIndex = effectivePage - minPage;
  const totalPages = maxPage - minPage + 1;

  return {
    effectivePage,
    canPrev: effectivePage > minPage,
    canNext: effectivePage < maxPage,
    pageLabel: `Seite ${pageIndex + 1} von ${totalPages}`,
    pageDates: pageDates.map((d) => ({ d, isBeforeAnchor: d < anchor, isPast: d < today })),
  };
};

const computeDefaultRoomTagsForFach = (rooms: Room[], fachId: number, fachOptions: FachItem[]): string[] => {
  if (!fachId) return [];
  const fachName = (fachOptions.find((f) => f.id === fachId)?.name || '').trim().toLowerCase();
  if (!fachName) return [];
  const tags = new Set<string>();
  rooms.forEach((room) => {
    (room.tags || []).forEach((tagRaw) => {
      const tag = String(tagRaw || '').trim();
      const normalizedTag = tag.toLowerCase();
      if (!tag) return;
      if (normalizedTag === fachName || normalizedTag.includes(fachName) || fachName.includes(normalizedTag)) {
        tags.add(tag);
      }
    });
  });
  return Array.from(tags);
};

export default function MigrationCaseActionWizardModal({
  open,
  caseId,
  actionType,
  onOpenChange,
  loadCaseDetails,
  onPreview,
  onExecute,
  onDone,
}: Readonly<Props>) {
  const isContractAdjustment = actionType === 'contract_adjustment';
  const steps = useMemo(() => {
    if (isContractAdjustment) {
      return ['Personen zuordnen', 'Unterricht', 'Raum', 'Datum', 'Pakete', 'Kommunikation', 'Übersicht'];
    }
    if (actionType === 'contract_termination') return ['Grund', 'Kommunikation', 'Zusammenfassung'];
    return ['Auftragsdetails', 'Kommunikation', 'Übersicht'];
  }, [isContractAdjustment, actionType]);
  const title = isContractAdjustment ? 'Vertragsanpassung starten' : actionType === 'contract_termination' ? 'Vertrag beenden' : 'Auftragsanpassung';

  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);

  const [details, setDetails] = useState<MigrationCaseDetailsResponse | null>(null);
  const [resolvedCustomerWpUserId, setResolvedCustomerWpUserId] = useState(0);
  const [genericPayload, setGenericPayload] = useState<Record<string, unknown>>({
    reason: '',
    communication: { email_mandatory: true, sms_optional: false, sms_text: '' },
  });

  const [personAssignment, setPersonAssignment] = useState<Record<PersonRole, { mode: AssignmentMode; legacy_person_id: number; wp_user_id: number }>>({
    student: { mode: 'auto', legacy_person_id: 0, wp_user_id: 0 },
    contract_partner: { mode: 'auto', legacy_person_id: 0, wp_user_id: 0 },
  });
  const [legacyUpdates, setLegacyUpdates] = useState<Record<PersonRole, {
    salutation: string;
    company_name: string;
    first_name: string;
    last_name: string;
    birth_date: string;
    email: string;
    phone: string;
    address_street: string;
    address_zip: string;
    address_city: string;
    address_country: string;
    newsletter_opt_in: boolean;
    preferred_contact_email: boolean;
    preferred_contact_phone: boolean;
  }>>({
    student: {
      salutation: '',
      company_name: '',
      first_name: '',
      last_name: '',
      birth_date: '',
      email: '',
      phone: '',
      address_street: '',
      address_zip: '',
      address_city: '',
      address_country: 'Deutschland',
      newsletter_opt_in: false,
      preferred_contact_email: true,
      preferred_contact_phone: false,
    },
    contract_partner: {
      salutation: '',
      company_name: '',
      first_name: '',
      last_name: '',
      birth_date: '',
      email: '',
      phone: '',
      address_street: '',
      address_zip: '',
      address_city: '',
      address_country: 'Deutschland',
      newsletter_opt_in: false,
      preferred_contact_email: true,
      preferred_contact_phone: false,
    },
  });

  const [step1ActiveSection, setStep1ActiveSection] = useState<Step1Section>('unterricht');
  const [timetableBaseDate] = useState<Date>(new Date());
  const [timetableDayOfWeek, setTimetableDayOfWeek] = useState<number>(new Date().getDay() === 0 ? 7 : new Date().getDay());
  const [fachOptions, setFachOptions] = useState<FachItem[]>([]);
  const [teacherOptions, setTeacherOptions] = useState<LehrerOption[]>([]);
  const [teacherAvailability, setTeacherAvailability] = useState<TeacherAvailabilitySlot[]>([]);
  const [teacherAvailabilityLoading, setTeacherAvailabilityLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<SelectedSlot>(null);
  const [datePage, setDatePage] = useState(0);
  const [nextAvailableDate, setNextAvailableDate] = useState('');
  const [nextAvailableLoading, setNextAvailableLoading] = useState(false);
  const [date, setDate] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startDateManuallySelected, setStartDateManuallySelected] = useState(false);

  const [teacherDayLoading, setTeacherDayLoading] = useState(false);
  const [teacherDayError, setTeacherDayError] = useState('');
  const [hasTeacherCollision, setHasTeacherCollision] = useState(false);
  const [teacherRoomIdsForDate, setTeacherRoomIdsForDate] = useState<Set<number>>(() => new Set<number>());

  const [rooms, setRooms] = useState<Room[]>([]);
  const [roomId, setRoomId] = useState<number | undefined>(undefined);
  const [roomUnitsForDate, setRoomUnitsForDate] = useState<RoomUnit[]>([]);
  const [roomUnitsLoading, setRoomUnitsLoading] = useState(false);
  const [roomUnitsError, setRoomUnitsError] = useState('');
  const [roomPreviewOpen, setRoomPreviewOpen] = useState(false);
  const [roomPreview, setRoomPreview] = useState<Room | null>(null);

  const [lesson, setLesson] = useState({
    teacher_wp_user_id: 0,
    teacher_name: '',
    teacher_id: 0,
    fach_id: 0,
    fach_name: '',
    unterrichtsform_id: 0,
    unterrichtsform_name: '',
    weekday: 1,
    start_time: '',
    room_id: 0,
    room_name: '',
  });

  const [packagePrices, setPackagePrices] = useState<PackagePrice[]>([]);
  // Kontingent auf dem Vertragskonto (ADR-0003, Phase 6): Tarife statt Altpakete.
  const [billingOptions, setBillingOptions] = useState<EnrollmentBillingOptions | null>(null);
  const [intervalWeeks, setIntervalWeeks] = useState<number>(1);
  const [couponEnabled, setCouponEnabled] = useState(false);
  const contingentTariffs = billingOptions?.contingentTariffs ?? [];
  const periodicTariff = billingOptions?.model === 'periodic' ? (billingOptions.tariff ?? null) : null;
  const [couponAmountEuro, setCouponAmountEuro] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [couponGenerating, setCouponGenerating] = useState(false);
  const [openAppointmentsEnabled, setOpenAppointmentsEnabled] = useState(true);
  const [openLehrerAbsagen, setOpenLehrerAbsagen] = useState(0);
  const [openAussetzen, setOpenAussetzen] = useState(0);
  const [openAppointmentsResolution, setOpenAppointmentsResolution] = useState<
    'schedule_before_start' | 'credit' | 'none_open' | 'skip' | ''
  >('');
  const [historyText, setHistoryText] = useState<string>('');
  const [historyTouched, setHistoryTouched] = useState<boolean>(false);
  const [communicationDraft, setCommunicationDraft] = useState<{
    email_mandatory: true;
    sms_optional: boolean;
    sms_text: string;
    groups?: Record<string, GroupDraftState>;
  }>({
    email_mandatory: true,
    sms_optional: false,
    sms_text: '',
  });
  const hydrationDoneRef = useRef(false);
  const lastSavedDraftRef = useRef('');
  const [selectedRoomTags, setSelectedRoomTags] = useState<string[]>([]);
  const [roomTagsUserModified, setRoomTagsUserModified] = useState(false);
  const [roomSearchQuery, setRoomSearchQuery] = useState('');

  const openAppointmentsTotal = openLehrerAbsagen + openAussetzen;
  const hasOpenAppointmentsDecision = openAppointmentsEnabled && openAppointmentsTotal > 0;
  const step1UnterrichtCollapsed = step1ActiveSection !== 'unterricht';
  const step1TerminplanCollapsed = step1ActiveSection !== 'terminplan';
  const startTime = lesson.start_time;
  const selectedUnterrichtsformDurationMin = useMemo(() => {
    const options = details?.target_options?.unterrichtsformen ?? [];
    const selected = options.find((u) => u.id === lesson.unterrichtsform_id);
    const rawDuration = Number(selected?.dauer ?? 0);
    return rawDuration > 0 ? rawDuration : 30;
  }, [details?.target_options?.unterrichtsformen, lesson.unterrichtsform_id]);
  const endTime = startTime ? addMinutes(startTime, selectedUnterrichtsformDurationMin) : '';
  const selectedSubjectName = fachOptions.find((f) => f.id === lesson.fach_id)?.name || '—';
  const selectedTeacher = teacherOptions.find((t) => t.id === lesson.teacher_id);
  const teacherSummaryName = selectedTeacher ? `${selectedTeacher.vorname} ${selectedTeacher.nachname}` : '—';
  const datePageModel = useMemo(
    () => computeDatePageModel(selectedSlot, nextAvailableDate || startDate, datePage, 8),
    [selectedSlot, nextAvailableDate, startDate, datePage],
  );
  const roomCandidateDate = date || startDate || nextAvailableDate || firstOfNextMonth();
  const allRoomTags = useMemo(() => {
    const tagSet = new Set<string>();
    rooms
      .filter((room) => room.aktiv)
      .forEach((room) => {
        (room.tags || []).forEach((tagRaw) => {
          const tag = String(tagRaw || '').trim();
          if (tag) tagSet.add(tag);
        });
      });
    return Array.from(tagSet).sort((a, b) => a.localeCompare(b, 'de'));
  }, [rooms]);
  const filteredRoomsForFach = useMemo(() => {
    const active = rooms.filter((r) => r.aktiv);
    const query = roomSearchQuery.trim().toLowerCase();
    const queryFiltered = query
      ? active.filter((room) => {
          const address = String(
            room.address || [room.address_street, room.address_house_number, room.address_postal_code, room.address_city].filter(Boolean).join(' ')
          ).toLowerCase();
          return room.name.toLowerCase().includes(query) || address.includes(query);
        })
      : active;
    if (selectedRoomTags.length === 0) return queryFiltered;
    const selectedNormalized = new Set(selectedRoomTags.map((tag) => tag.trim().toLowerCase()));
    return queryFiltered.filter((room) => {
      const roomTags = new Set((room.tags || []).map((tag) => String(tag || '').trim().toLowerCase()));
      return Array.from(selectedNormalized).every((selectedTag) => roomTags.has(selectedTag));
    });
  }, [rooms, selectedRoomTags, roomSearchQuery]);
  const teacherRoomsOnSelectedDay = teacherRoomIdsForDate;
  const hasTeacherAvailability = teacherAvailability.length > 0;
  const teacherAvailabilityHint = teacherAvailabilityLoading
    ? 'Freigabezeiten werden geladen…'
    : hasTeacherAvailability
      ? 'Freigabezeiten sind im Terminplan grün hinterlegt.'
      : 'Lehrer hat keine Freigabezeiten gepflegt — alle Slots blockiert';
  const canShowTerminplan = lesson.fach_id > 0 && lesson.teacher_id > 0;
  const missingScheduleSelectionText = lesson.fach_id > 0 ? 'Bitte zuerst einen Lehrer auswählen.' : `Bitte zuerst ${getSubjectLabels().singular} auswählen.`;
  const previewStudentName = `${legacyUpdates.student.first_name} ${legacyUpdates.student.last_name}`.trim() || 'Schüler';
  const previewEvent = selectedSlot && lesson.fach_id > 0 && lesson.teacher_id > 0
    ? {
        dayOfWeek: selectedSlot.dayOfWeek,
        startTime: selectedSlot.startTime.slice(0, 5),
        endTime: selectedSlot.endTime.slice(0, 5),
        title: previewStudentName,
        subtitle: previewStudentName,
        fachName: selectedSubjectName !== '—' ? selectedSubjectName : undefined,
      }
    : null;

  useEffect(() => {
    if (!open) return;
    hydrationDoneRef.current = false;
    lastSavedDraftRef.current = '';
    setStep(1);
    setWarnings([]);
    setError(null);
    if (!isContractAdjustment) return;
    const defaultStart = firstOfNextMonth();
    setStartDate(defaultStart);
    setDate(defaultStart);
    setStartDateManuallySelected(false);
    setDatePage(0);
    setSelectedRoomTags([]);
    setRoomTagsUserModified(false);
        setRoomSearchQuery('');
        setStep1ActiveSection('unterricht');
        setCommunicationDraft({
      email_mandatory: true,
      sms_optional: false,
      sms_text: '',
    });
    setResolvedCustomerWpUserId(0);
    setIntervalWeeks(1);
    setHistoryText('');
    setHistoryTouched(false);
    setTeacherRoomIdsForDate(new Set<number>());
  }, [open, isContractAdjustment]);

  useEffect(() => {
    if (!open || roomTagsUserModified) return;
    setSelectedRoomTags(computeDefaultRoomTagsForFach(rooms, lesson.fach_id, fachOptions));
  }, [open, rooms, lesson.fach_id, fachOptions, roomTagsUserModified]);

  // Auto-Copy Student → Vertragspartner Adresse wenn:
  //   - Schueler ist minderjaehrig (< 18) und
  //   - Vertragspartner-Adresse ist leer und
  //   - Schueler-Adresse ist befuellt
  // Hintergrund: bei Kindern hat im Alt-System oft nur der Schueler eine Adresse,
  // der Vertragspartner ist eine separate Person ohne hinterlegte Anschrift.
  useEffect(() => {
    if (!open || !isContractAdjustment) return;
    const studentAge = calculateAge(legacyUpdates.student.birth_date);
    if (studentAge === null || studentAge >= 18) return;
    const studentStreet = legacyUpdates.student.address_street.trim();
    const studentZip = legacyUpdates.student.address_zip.trim();
    const studentCity = legacyUpdates.student.address_city.trim();
    if (!studentStreet && !studentZip && !studentCity) return;
    const partnerStreet = legacyUpdates.contract_partner.address_street.trim();
    const partnerZip = legacyUpdates.contract_partner.address_zip.trim();
    const partnerCity = legacyUpdates.contract_partner.address_city.trim();
    if (partnerStreet || partnerZip || partnerCity) return;
    setLegacyUpdates((prev) => ({
      ...prev,
      contract_partner: {
        ...prev.contract_partner,
        address_street: prev.student.address_street,
        address_zip: prev.student.address_zip,
        address_city: prev.student.address_city,
        address_country: prev.student.address_country || prev.contract_partner.address_country || 'Deutschland',
      },
    }));
  }, [
    open,
    isContractAdjustment,
    legacyUpdates.student.birth_date,
    legacyUpdates.student.address_street,
    legacyUpdates.student.address_zip,
    legacyUpdates.student.address_city,
    legacyUpdates.student.address_country,
    legacyUpdates.contract_partner.address_street,
    legacyUpdates.contract_partner.address_zip,
    legacyUpdates.contract_partner.address_city,
  ]);

  useEffect(() => {
    if (!open || !caseId || !isContractAdjustment) return;
    let active = true;
    setIsLoadingDetails(true);
    loadCaseDetails(caseId)
      .then(async (res) => {
        if (!active) return;
        setDetails(res);
        setResolvedCustomerWpUserId(Number(res.case.wp_user_id ?? 0));
        const snapshot = (res.legacy_data ?? {}) as Record<string, unknown>;
        const contractWizardDraft = (
          snapshot.office_contract_adjustment_wizard &&
          typeof snapshot.office_contract_adjustment_wizard === 'object'
        ) ? (snapshot.office_contract_adjustment_wizard as Partial<ContractAdjustmentWizardDraft>) : null;
        const relatedPeople = Array.isArray(snapshot.legacy_related_people) ? (snapshot.legacy_related_people as Array<Record<string, unknown>>) : [];
        const student = relatedPeople.find((p) => String(p.role) === 'student');
        const partner = relatedPeople.find((p) => String(p.role) === 'contract_partner') ?? relatedPeople.find((p) => String(p.role) === 'payer');
        const studentBirthDate = String(snapshot.legacy_student_birth_date ?? '');
        const studentAge = calculateAge(studentBirthDate);
        const studentIsAdult = studentAge !== null && studentAge >= 18;
        const studentLegacyPersonId = Number(student?.legacy_person_id ?? snapshot.legacy_person_id ?? 0);
        const partnerLegacyPersonId = Number(partner?.legacy_person_id ?? snapshot.legacy_contract_partner_legacy_person_id ?? 0);
        const contractPartnerLegacyPersonId = studentIsAdult && studentLegacyPersonId > 0 ? studentLegacyPersonId : partnerLegacyPersonId;
        const teacherName = String(snapshot.legacy_teacher_name ?? '');
        const roomName = String(snapshot.legacy_room_name ?? '');
        setOpenLehrerAbsagen(Number(snapshot.legacy_lehrer_absagen ?? 0));
        setOpenAussetzen(Number(snapshot.legacy_aussetzen ?? 0));
        setOpenAppointmentsEnabled(true);

        setPersonAssignment({
          student: {
            mode: 'auto',
            legacy_person_id: studentLegacyPersonId,
            wp_user_id: Number(res.case.wp_user_id ?? 0),
          },
          contract_partner: {
            mode: 'auto',
            legacy_person_id: contractPartnerLegacyPersonId,
            wp_user_id: Number(res.case.wp_user_id ?? 0),
          },
        });
        setLegacyUpdates({
          student: {
            salutation: String(snapshot.legacy_student_salutation ?? ''),
            first_name: String(snapshot.legacy_student_first_name ?? ''),
            last_name: String(snapshot.legacy_student_last_name ?? ''),
            birth_date: studentBirthDate,
            phone: String(snapshot.legacy_student_phone ?? ''),
            email: String(snapshot.legacy_student_email ?? ''),
            address_street: String(snapshot.legacy_student_address_street ?? ''),
            address_zip: String(snapshot.legacy_student_address_zip ?? ''),
            address_city: String(snapshot.legacy_student_address_city ?? ''),
            address_country: String(snapshot.legacy_student_address_country ?? 'Deutschland'),
            newsletter_opt_in: Boolean(snapshot.legacy_student_newsletter_opt_in ?? false),
            preferred_contact_email: snapshot.legacy_student_preferred_contact_email !== false,
            preferred_contact_phone: Boolean(snapshot.legacy_student_preferred_contact_phone ?? false),
          },
          contract_partner: {
            salutation: studentIsAdult
              ? String(snapshot.legacy_student_salutation ?? '')
              : String(snapshot.legacy_contract_partner_salutation ?? ''),
            company_name: '',
            first_name: studentIsAdult
              ? String(snapshot.legacy_student_first_name ?? '')
              : String(snapshot.legacy_contract_partner_first_name ?? ''),
            last_name: studentIsAdult
              ? String(snapshot.legacy_student_last_name ?? '')
              : String(snapshot.legacy_contract_partner_last_name ?? ''),
            birth_date: studentIsAdult
              ? studentBirthDate
              : String(snapshot.legacy_contract_partner_birth_date ?? ''),
            phone: studentIsAdult
              ? String(snapshot.legacy_student_phone ?? '')
              : String(snapshot.legacy_contract_partner_phone ?? ''),
            email: studentIsAdult
              ? String(snapshot.legacy_student_email ?? '')
              : String(snapshot.legacy_contract_partner_email ?? ''),
            address_street: studentIsAdult
              ? String(snapshot.legacy_student_address_street ?? '')
              : String(snapshot.legacy_contract_partner_address_street ?? ''),
            address_zip: studentIsAdult
              ? String(snapshot.legacy_student_address_zip ?? '')
              : String(snapshot.legacy_contract_partner_address_zip ?? ''),
            address_city: studentIsAdult
              ? String(snapshot.legacy_student_address_city ?? '')
              : String(snapshot.legacy_contract_partner_address_city ?? ''),
            address_country: studentIsAdult
              ? String(snapshot.legacy_student_address_country ?? 'Deutschland')
              : String(snapshot.legacy_contract_partner_address_country ?? 'Deutschland'),
            newsletter_opt_in: studentIsAdult
              ? Boolean(snapshot.legacy_student_newsletter_opt_in ?? false)
              : Boolean(snapshot.legacy_contract_partner_newsletter_opt_in ?? false),
            preferred_contact_email: true,
            preferred_contact_phone: studentIsAdult
              ? Boolean(snapshot.legacy_student_preferred_contact_phone ?? false)
              : Boolean(snapshot.legacy_contract_partner_preferred_contact_phone ?? false),
          },
        });

        let loadedFaecher: FachItem[] = [];
        let loadedTeachers: LehrerOption[] = [];
        try {
          loadedFaecher = await masterdataApi.faecher(res.case.standort_id);
          if (active) setFachOptions(loadedFaecher.filter((f) => f.aktiv !== false));
        } catch {
          if (active) setFachOptions([]);
        }
        try {
          loadedTeachers = await lehrerApi.list({ standortId: res.case.standort_id });
          if (active) setTeacherOptions((loadedTeachers || []).filter((t) => t.aktiv !== false));
        } catch {
          if (active) setTeacherOptions([]);
        }
        let resolvedInitialRoomId = 0;
        let resolvedInitialRoomName = roomName;
        try {
          const roomList = await getRooms(res.case.standort_id);
          if (active) setRooms(roomList);
          const roomByName = roomList.find((r) => r.name.trim().toLowerCase() === roomName.trim().toLowerCase());
          resolvedInitialRoomId = roomByName?.id ?? 0;
          resolvedInitialRoomName = roomByName?.name ?? roomName;
          if (active) setRoomId(roomByName?.id);
          if (active) {
            setLesson((prev) => ({
              ...prev,
              room_id: resolvedInitialRoomId,
              room_name: resolvedInitialRoomName,
            }));
          }
        } catch {
          if (active) setRooms([]);
        }

        const teacherByName = loadedTeachers.find(
          (t) => `${t.vorname} ${t.nachname}`.trim().toLowerCase() === teacherName.trim().toLowerCase(),
        );
        // 1. Priorität: vom Backend per legacy_id_mappings aufgelöste neue
        //    System-Fach-ID (snapshot.resolved_fach_id). Garantiert konsistent.
        // 2. Fallback: Name-Match (legacy_fach_name bzw. LEGACY_FACH_MASTER).
        // legacy_fach_id (ALT-System-ID) wird NICHT direkt als neue System-ID
        // verwendet – siehe Bug aus Mai 2026.
        const resolvedFachId = Number(snapshot.resolved_fach_id ?? 0);
        const resolvedFachName = String(snapshot.resolved_fach_name ?? '').trim();
        const fachNameFromSnapshot = String(snapshot.legacy_fach_name ?? '').trim();
        const fachName = resolvedFachName
          || fachNameFromSnapshot
          || resolveLegacyFachName(Number(snapshot.legacy_fach_id ?? 0), null);
        const fachByMapping = resolvedFachId > 0
          ? loadedFaecher.find((f) => f.id === resolvedFachId)
          : undefined;
        const fachByName = fachByMapping
          ? undefined
          : (fachName
              ? loadedFaecher.find((f) => f.name.trim().toLowerCase() === fachName.trim().toLowerCase())
              : undefined);
        const resolvedFach = fachByMapping ?? fachByName;
        const legacyUformName = String(snapshot.legacy_uform_name ?? '');
        const uformOptions = res.target_options?.unterrichtsformen ?? [];
        // Dauer lokal aus den geladenen Optionen aufloesen — NICHT ueber das
        // selectedUnterrichtsformDurationMin-Memo, sonst haengt dieser Lade-
        // Effekt an lesson.unterrichtsform_id und laedt + rehydriert bei jeder
        // Unterrichtsform-Aenderung neu (Auswahl "springt zurueck").
        const resolveUformDurationMin = (uformId: number): number => {
          const raw = Number(uformOptions.find((u) => u.id === uformId)?.dauer ?? 0);
          return raw > 0 ? raw : 30;
        };
        const legacyUformId = Number(snapshot.legacy_uform_id ?? 0);
        const mappedUformId = Number(snapshot.resolved_uform_id ?? 0);
        const resolvedUformId = resolveUnterrichtsformId(legacyUformId, legacyUformName, mappedUformId, uformOptions);
        const resolvedUformName =
          uformOptions.find((u) => u.id === resolvedUformId)?.name ||
          legacyUformName ||
          '';
        const initialStartDate = firstOfNextMonth();
        const legacyWeekday = Number(snapshot.legacy_weekday ?? 0);
        const legacyStartTime = String(snapshot.legacy_start_time ?? '').slice(0, 5);
        if (legacyWeekday > 0 && legacyStartTime) {
          setSelectedSlot({
            dayOfWeek: legacyWeekday,
            startTime: `${legacyStartTime}:00`,
            endTime: `${addMinutes(legacyStartTime, resolveUformDurationMin(resolvedUformId))}:00`,
          });
          setTimetableDayOfWeek(legacyWeekday);
        } else {
          setSelectedSlot(null);
        }

        setStartDate(initialStartDate);
        setDate(initialStartDate);
        setStartDateManuallySelected(false);
        setLesson({
          teacher_wp_user_id: Number(teacherByName?.wp_user_id ?? 0),
          teacher_name: teacherName,
          teacher_id: Number(teacherByName?.id ?? 0),
          // Kein Fallback auf snapshot.legacy_fach_id: das ist die Alt-System-ID
          // und passt nicht zu den fach_id im neuen Master-Katalog.
          fach_id: Number(resolvedFach?.id ?? 0),
          fach_name: resolvedFach?.name ?? fachName,
          unterrichtsform_id: resolvedUformId,
          unterrichtsform_name: resolvedUformName,
          weekday: legacyWeekday > 0 ? legacyWeekday : 1,
          start_time: legacyStartTime,
          // resolvedInitialRoomId/Name kommt aus dem rooms-Lookup oben
          // (Match auf snapshot.legacy_room_name). Ohne diesen Re-Fill wuerde
          // das Komplett-Replace hier den von Z. ~631 gesetzten room_id wieder
          // auf 0 zuruecksetzen und der Snapshot speichert dann nur room_name
          // ohne ID — was beim Materialisieren default_raum_id=NULL ergibt.
          room_id: resolvedInitialRoomId,
          room_name: resolvedInitialRoomName,
        });

        if (contractWizardDraft?.action_type === 'contract_adjustment') {
          const persistedPayload = (contractWizardDraft.draft_payload ?? null) as MigrationContractAdjustmentPayload | null;
          if (persistedPayload) {
            const persistedStudent = persistedPayload.person_assignment?.student;
            const persistedPartner = persistedPayload.person_assignment?.contract_partner;
            if (persistedStudent || persistedPartner) {
              setPersonAssignment((prev) => ({
                student: {
                  mode: (persistedStudent?.mode as AssignmentMode) ?? prev.student.mode,
                  legacy_person_id: Number(persistedStudent?.legacy_person_id ?? prev.student.legacy_person_id ?? 0),
                  wp_user_id: Number(persistedStudent?.wp_user_id ?? prev.student.wp_user_id ?? 0),
                },
                contract_partner: {
                  mode: (persistedPartner?.mode as AssignmentMode) ?? prev.contract_partner.mode,
                  legacy_person_id: Number(persistedPartner?.legacy_person_id ?? prev.contract_partner.legacy_person_id ?? 0),
                  wp_user_id: Number(persistedPartner?.wp_user_id ?? prev.contract_partner.wp_user_id ?? 0),
                },
              }));
            }

            if (persistedPayload.legacy_updates) {
              setLegacyUpdates((prev) => ({
                student: { ...prev.student, ...(persistedPayload.legacy_updates?.student ?? {}) },
                contract_partner: { ...prev.contract_partner, ...(persistedPayload.legacy_updates?.contract_partner ?? {}) },
              }));
            }

            if (persistedPayload.lesson) {
              setLesson((prev) => ({
                ...prev,
                teacher_wp_user_id: Number(persistedPayload.lesson.teacher_wp_user_id ?? prev.teacher_wp_user_id),
                teacher_name: String(persistedPayload.lesson.teacher_name ?? prev.teacher_name),
                fach_id: Number(persistedPayload.lesson.fach_id ?? prev.fach_id),
                fach_name: String(persistedPayload.lesson.fach_name ?? prev.fach_name),
                unterrichtsform_id: Number(persistedPayload.lesson.unterrichtsform_id ?? prev.unterrichtsform_id),
                unterrichtsform_name: String(persistedPayload.lesson.unterrichtsform_name ?? prev.unterrichtsform_name),
                weekday: Number(persistedPayload.lesson.weekday ?? prev.weekday),
                start_time: String(persistedPayload.lesson.start_time ?? prev.start_time),
                room_id: Number(persistedPayload.lesson.room_id ?? prev.room_id),
                room_name: String(persistedPayload.lesson.room_name ?? prev.room_name),
              }));
              const persistedInterval = Number(persistedPayload.lesson.interval_weeks ?? 0);
              if (persistedInterval > 0) setIntervalWeeks(persistedInterval);
              const persistedStart = String(persistedPayload.lesson.start_time ?? '').slice(0, 5);
              const persistedWeekday = Number(persistedPayload.lesson.weekday ?? 0);
              if (persistedWeekday > 0 && persistedStart) {
                setSelectedSlot({
                  dayOfWeek: persistedWeekday,
                  startTime: `${persistedStart}:00`,
                  endTime: `${addMinutes(persistedStart, resolveUformDurationMin(Number(persistedPayload.lesson.unterrichtsform_id ?? 0)))}:00`,
                });
                setTimetableDayOfWeek(persistedWeekday);
              }
              if (Number(persistedPayload.lesson.room_id ?? 0) > 0) {
                setRoomId(Number(persistedPayload.lesson.room_id));
              }
            }

            if (persistedPayload.start_date) {
              const persistedStartDate = String(persistedPayload.start_date);
              setStartDate(persistedStartDate);
              setDate(persistedStartDate);
            }

            // package_selection wird nicht mehr aus dem Draft hydriert –
            // bei Migration-Cases waehlt der Kunde immer im Kundenbereich.

            if (persistedPayload.communication) {
              setCommunicationDraft((prev) => ({
                ...prev,
                sms_optional: Boolean(persistedPayload.communication.sms_optional ?? false),
                sms_text: String(persistedPayload.communication.sms_text ?? ''),
                groups: persistedPayload.communication.groups as Record<string, GroupDraftState> | undefined,
              }));
            }

            const persistedHistory = persistedPayload.history?.description;
            if (typeof persistedHistory === 'string' && persistedHistory.trim() !== '') {
              setHistoryText(persistedHistory);
              setHistoryTouched(true);
            }

            const persistedCoupon = String(persistedPayload.coupon_code ?? '').trim();
            if (persistedCoupon !== '') {
              setCouponEnabled(true);
              setCouponCode(persistedCoupon);
            }
            const persistedOpen = persistedPayload.open_appointments;
            if (persistedOpen) {
              setOpenAppointmentsEnabled(Boolean(persistedOpen.transfer ?? true));
              setOpenLehrerAbsagen(Number(persistedOpen.lehrer_absagen ?? 0));
              setOpenAussetzen(Number(persistedOpen.aussetzen ?? 0));
              const res = persistedOpen.resolution;
              if (res === 'schedule_before_start' || res === 'credit' || res === 'none_open' || res === 'skip') {
                setOpenAppointmentsResolution(res);
              }
            }
          }

          setStep(Math.min(steps.length, Math.max(1, Number(contractWizardDraft.current_step ?? 1))));
          setStep1ActiveSection(contractWizardDraft.step1_active_section ?? 'unterricht');
          setTimetableDayOfWeek(Number(contractWizardDraft.timetable_day_of_week ?? 1));
          setStartDateManuallySelected(Boolean(contractWizardDraft.start_date_manually_selected));
          setResolvedCustomerWpUserId(Number(contractWizardDraft.wp_user_id ?? res.case.wp_user_id ?? 0));
          lastSavedDraftRef.current = JSON.stringify(contractWizardDraft);
        }
        hydrationDoneRef.current = true;
      })
      .catch((e) => {
        if (!active) return;
        setError(e instanceof Error ? e.message : 'Case-Details konnten nicht geladen werden');
      })
      .finally(() => {
        if (active) setIsLoadingDetails(false);
      });
    return () => {
      active = false;
    };
  }, [open, caseId, isContractAdjustment, loadCaseDetails, steps.length]);

  useEffect(() => {
    if (!open || !isContractAdjustment || !details?.case.standort_id || lesson.fach_id <= 0) return;
    let active = true;
    lehrerApi
      .list({ standortId: details.case.standort_id, fachId: lesson.fach_id })
      .then((items) => {
        if (!active) return;
        setTeacherOptions((items || []).filter((t) => t.aktiv !== false));
      })
      .catch(() => {
        if (active) setTeacherOptions([]);
      });
    return () => {
      active = false;
    };
  }, [open, isContractAdjustment, details?.case.standort_id, lesson.fach_id]);

  useEffect(() => {
    if (!open || !isContractAdjustment || !details?.case.standort_id || lesson.teacher_wp_user_id <= 0) {
      setTeacherAvailability([]);
      return;
    }
    let active = true;
    setTeacherAvailabilityLoading(true);
    personenApi
      .getTeacherAvailability(lesson.teacher_wp_user_id, details.case.standort_id)
      .then((slots) => {
        if (active) setTeacherAvailability(slots || []);
      })
      .catch(() => {
        if (active) setTeacherAvailability([]);
      })
      .finally(() => {
        if (active) setTeacherAvailabilityLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open, isContractAdjustment, details?.case.standort_id, lesson.teacher_wp_user_id]);

  useEffect(() => {
    if (!open || !isContractAdjustment || !selectedSlot || lesson.teacher_wp_user_id <= 0) {
      setNextAvailableDate('');
      return;
    }
    let cancelled = false;
    setNextAvailableLoading(true);
    const run = async () => {
      try {
        const todayYmd = toApiDate(new Date());
        const endDate = addDaysYmd(todayYmd, 56);
        const candidates: string[] = [];
        const start = new Date(`${addDaysYmd(todayYmd, -28)}T00:00:00`);
        const end = new Date(`${endDate}T00:00:00`);
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          const ymd = toApiDate(d);
          const dow = ((d.getDay() + 6) % 7) + 1;
          if (dow === selectedSlot.dayOfWeek) candidates.push(ymd);
        }

        const [units, trials] = await Promise.all([
          unterrichtseinheitenApi.list({ teacherId: lesson.teacher_wp_user_id, start: todayYmd, end: endDate }),
          fetchTrials(todayYmd, endDate),
        ]);
        const busyByDate = new Map<string, Array<{ s: string; e: string }>>();
        (units || []).forEach((u) => {
          const ymd = u.start_zeit.slice(0, 10);
          const arr = busyByDate.get(ymd) || [];
          arr.push({ s: u.start_zeit.slice(11, 16), e: u.end_zeit.slice(11, 16) });
          busyByDate.set(ymd, arr);
        });
        (trials || [])
          .filter((t) => String(t.teacherId || '') === String(lesson.teacher_wp_user_id))
          .forEach((t) => {
            const arr = busyByDate.get(t.date) || [];
            arr.push({ s: t.startTime.slice(0, 5), e: t.endTime.slice(0, 5) });
            busyByDate.set(t.date, arr);
          });

        const slotStart = selectedSlot.startTime.slice(0, 5);
        const slotEnd = selectedSlot.endTime.slice(0, 5);
        const firstFree = candidates.find((d) => {
          if (d < todayYmd) return false;
          const busy = busyByDate.get(d) || [];
          return !busy.some((b) => overlaps(slotStart, slotEnd, b.s, b.e));
        }) || candidates.find((d) => d >= todayYmd) || '';
        if (!cancelled) setNextAvailableDate(firstFree);
      } catch {
        if (!cancelled) setNextAvailableDate('');
      } finally {
        if (!cancelled) setNextAvailableLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [open, isContractAdjustment, selectedSlot, lesson.teacher_wp_user_id]);

  useEffect(() => {
    if (!open || !isContractAdjustment || lesson.teacher_wp_user_id <= 0 || !date || !startTime) {
      setHasTeacherCollision(false);
      setTeacherDayError('');
      setTeacherRoomIdsForDate(new Set<number>());
      return;
    }
    let cancelled = false;
    setTeacherDayLoading(true);
    setTeacherDayError('');
    Promise.all([
      unterrichtseinheitenApi.list({ teacherId: lesson.teacher_wp_user_id, start: date, end: date }),
      fetchTrials(date, date),
    ])
      .then(([units, trials]) => {
        if (cancelled) return;
        const end = endTime || addMinutes(startTime, 30);
        const unitOverlap = (units || []).some((u) => overlaps(startTime, end, u.start_zeit.slice(11, 16), u.end_zeit.slice(11, 16)));
        const teacherTrials = (trials || []).filter((t) => String(t.teacherId || '') === String(lesson.teacher_wp_user_id));
        const trialOverlap = teacherTrials.some((t) => overlaps(startTime, end, t.startTime.slice(0, 5), t.endTime.slice(0, 5)));
        setHasTeacherCollision(unitOverlap || trialOverlap);
        const roomSet = new Set<number>();
        (units || []).forEach((u) => {
          const id = Number(u.raum_id ?? 0);
          if (Number.isFinite(id) && id > 0) roomSet.add(id);
        });
        teacherTrials.forEach((t) => {
          const id = Number((t as { roomId?: number | string | null }).roomId ?? 0);
          if (Number.isFinite(id) && id > 0) roomSet.add(id);
        });
        setTeacherRoomIdsForDate(roomSet);
      })
      .catch(() => {
        if (!cancelled) {
          setHasTeacherCollision(false);
          setTeacherDayError('Lehrerbelegungen konnten nicht geladen werden.');
          setTeacherRoomIdsForDate(new Set<number>());
        }
      })
      .finally(() => {
        if (!cancelled) setTeacherDayLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, isContractAdjustment, lesson.teacher_wp_user_id, date, startTime, endTime]);

  useEffect(() => {
    if (!open || !isContractAdjustment || roomCandidateDate === '' || filteredRoomsForFach.length === 0) {
      setRoomUnitsForDate([]);
      return;
    }
    let active = true;
    setRoomUnitsLoading(true);
    setRoomUnitsError('');
    getRoomsUnits(
      filteredRoomsForFach.map((r) => r.id),
      roomCandidateDate,
      roomCandidateDate,
    )
      .then((units) => {
        if (active) setRoomUnitsForDate(units || []);
      })
      .catch(() => {
        if (active) {
          setRoomUnitsForDate([]);
          setRoomUnitsError('Raumbelegung konnte nicht geladen werden.');
        }
      })
      .finally(() => {
        if (active) setRoomUnitsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open, isContractAdjustment, roomCandidateDate, filteredRoomsForFach]);

  useEffect(() => {
    if (!open || !isContractAdjustment || lesson.unterrichtsform_id <= 0) {
      setBillingOptions(null);
      return;
    }
    let active = true;
    billingApi
      .enrollmentOptions({ unterrichtsformId: lesson.unterrichtsform_id })
      .then((options) => {
        if (active) setBillingOptions(options);
      })
      .catch(() => {
        // Ohne Berechtigung oder Tarife bleibt es bei den Altpaketen.
        if (active) setBillingOptions(null);
      });
    return () => {
      active = false;
    };
  }, [open, isContractAdjustment, lesson.unterrichtsform_id]);

  useEffect(() => {
    if (!open || !isContractAdjustment || lesson.unterrichtsform_id <= 0) {
      setPackagePrices([]);
      return;
    }
    let active = true;
    api
      .get<AccountingPackage[]>(`/api/unterrichtsformen/${lesson.unterrichtsform_id}/packages`)
      .then((items) => {
        if (!active) return;
        const followups = (items || []).filter((pkg) => pkg.special !== true);
        const sorted = [...followups].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
        const prices: PackagePrice[] = sorted.map((pkg) => {
          const totalImmediate = Number(pkg.totalCentsImmediate ?? pkg.immediatePriceCents ?? 0);
          const totalInstallment = Number(pkg.totalCentsInstallment ?? pkg.installmentPriceCents ?? 0);
          const configuredDiscount = Math.max(
            0,
            Math.round(Number(pkg.immediateDiscountPercent ?? pkg.discountPercent ?? 0)),
          );
          const originalTotal = totalInstallment > totalImmediate
            ? totalInstallment
            : configuredDiscount > 0 && totalImmediate > 0
              ? Math.round(totalImmediate / (1 - configuredDiscount / 100))
              : totalImmediate;
          return {
            package: pkg,
            pricePerUnitCents: pkg.units > 0 ? Math.round(totalImmediate / pkg.units) : 0,
            originalPricePerUnitCents: pkg.units > 0 ? Math.round(originalTotal / pkg.units) : 0,
            totalCents: totalImmediate,
            originalTotalCents: originalTotal,
            currency: 'EUR',
          };
        });
        setPackagePrices(prices);
      })
      .catch(() => {
        if (active) {
          setPackagePrices([]);
        }
      });
    return () => {
      active = false;
    };
  }, [open, isContractAdjustment, lesson.unterrichtsform_id]);

  useEffect(() => {
    if (!open || !isContractAdjustment || !selectedSlot) return;
    setLesson((prev) => {
      const nextWeekday = selectedSlot.dayOfWeek;
      const nextStart = selectedSlot.startTime.slice(0, 5);
      if (prev.weekday === nextWeekday && prev.start_time === nextStart) return prev;
      return { ...prev, weekday: nextWeekday, start_time: nextStart };
    });
  }, [open, isContractAdjustment, selectedSlot]);

  useEffect(() => {
    if (!open || !isContractAdjustment || !selectedSlot) return;
    const nextEndTime = `${addMinutes(selectedSlot.startTime.slice(0, 5), selectedUnterrichtsformDurationMin)}:00`;
    if (selectedSlot.endTime === nextEndTime) return;
    setSelectedSlot((prev) => (prev ? { ...prev, endTime: nextEndTime } : prev));
  }, [open, isContractAdjustment, selectedSlot, selectedUnterrichtsformDurationMin]);

  const weekdayName = useMemo(() => {
    const day = lesson.weekday;
    if (!day || day < 1 || day > 7) return '';
    return ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'][day - 1];
  }, [lesson.weekday]);

  const timeRangeLabel = useMemo(() => {
    if (!startTime || !selectedUnterrichtsformDurationMin) return '—';
    const end = addMinutes(startTime, selectedUnterrichtsformDurationMin);
    return `${startTime.slice(0, 5)} bis ${end} Uhr`;
  }, [startTime, selectedUnterrichtsformDurationMin]);

  const studentDisplayName = useMemo(() => {
    return `${legacyUpdates.student.first_name} ${legacyUpdates.student.last_name}`.trim() || 'Schüler';
  }, [legacyUpdates.student.first_name, legacyUpdates.student.last_name]);

  const computedHistoryText = useMemo(() => {
    const dateText = startDate ? formatDate(startDate) : 'offenes Datum';
    const timeText = startTime ? `${startTime.slice(0, 5)} Uhr` : 'offene Uhrzeit';
    return `Vertrag aus Migration für ${studentDisplayName} angepasst (${dateText}, ${timeText}).`;
  }, [studentDisplayName, startDate, startTime]);

  useEffect(() => {
    if (!open || !isContractAdjustment) return;
    if (historyTouched) return;
    setHistoryText(computedHistoryText);
  }, [open, isContractAdjustment, historyTouched, computedHistoryText]);

  useEffect(() => {
    if (!open || !isContractAdjustment || step !== 7 || historyTouched) return;
    if (!startDate || !startTime) return;
    let cancelled = false;
    void (async () => {
      try {
        const rendered = await historyTemplatesApi.render({
          template_key: 'migration.contract_adjustment.history',
          standort_id: details?.case.standort_id || undefined,
          vars: {
            studentName: studentDisplayName,
            teacherName: teacherSummaryName !== '—' ? teacherSummaryName : '',
            date: formatDate(startDate),
            startTime: startTime.slice(0, 5),
            endTime: endTime || addMinutes(startTime, selectedUnterrichtsformDurationMin),
            roomName: lesson.room_name || '',
            roomClause: lesson.room_name ? `im Raum ${lesson.room_name}` : '',
            vertragsnummer: String(details?.legacy_data?.legacy_contract_id ?? details?.case?.legacy_contract_id ?? ''),
            zahlerVorname: legacyUpdates.contract_partner.first_name,
            zahlerNachname: legacyUpdates.contract_partner.last_name,
            lehrerabsagen: openAppointmentsEnabled ? String(openLehrerAbsagen) : '0',
            aussetzen: openAppointmentsEnabled ? String(openAussetzen) : '0',
          },
        });
        if (cancelled) return;
        if (rendered.history) setHistoryText(rendered.history);
      } catch {
        // Template optional — wir behalten den computedHistoryText
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, isContractAdjustment, step, historyTouched, startDate, startTime, endTime, selectedUnterrichtsformDurationMin, studentDisplayName, teacherSummaryName, lesson.room_name, details?.case.standort_id]);

  useEffect(() => {
    if (!open || !isContractAdjustment || step !== 4 || !selectedSlot) return;
    if (startDateManuallySelected) return;
    const autoDate = firstMatchingWeekdayInNextMonth(selectedSlot.dayOfWeek);
    setDate(autoDate);
    setStartDate(autoDate);
  }, [open, isContractAdjustment, step, selectedSlot, startDateManuallySelected]);

  useEffect(() => {
    if (!open || !isContractAdjustment) return;
    const selected = rooms.find((r) => r.id === roomId);
    setLesson((prev) => ({
      ...prev,
      room_id: roomId ?? 0,
      room_name: selected?.name ?? prev.room_name,
    }));
  }, [open, isContractAdjustment, roomId, rooms]);

  const customerWpUserId = useMemo(() => {
    if (resolvedCustomerWpUserId > 0) return resolvedCustomerWpUserId;
    if (personAssignment.contract_partner.mode === 'manual' && personAssignment.contract_partner.wp_user_id > 0) {
      return personAssignment.contract_partner.wp_user_id;
    }
    return Number(details?.case?.wp_user_id ?? 0);
  }, [resolvedCustomerWpUserId, personAssignment.contract_partner, details?.case?.wp_user_id]);

  const composerGroups = useMemo<RecipientGroup[]>(() => {
    if (!details?.case?.standort_id || customerWpUserId <= 0) return [];
    // Vollständigen Namen aus den Vertragspartner-Daten des Wizards bauen.
    // details.case.wp_user_name = CONCAT(first_name, ' ', last_name) — fehlt
    // das first_name-Meta, bleibt nur der Nachname übrig.
    const partnerFullName = `${legacyUpdates.contract_partner.first_name} ${legacyUpdates.contract_partner.last_name}`.trim();
    return [{
      key: 'customer',
      label: 'Kunde',
      recipients: [{
        id: `person:${customerWpUserId}`,
        type: 'person',
        wpUserId: customerWpUserId,
        name: partnerFullName || details.case.wp_user_name || 'Kunde',
      }],
      templateKeys: { email: 'migration.onboarding.start' },
      vars: {
        studentName: `${legacyUpdates.student.first_name} ${legacyUpdates.student.last_name}`.trim(),
        teacherName: teacherSummaryName,
        date,
        startTime,
        roomName: lesson.room_name,
        couponCode: couponEnabled && couponCode ? couponCode : '',
        couponBlockHtml: couponEnabled && couponCode
          ? `<p>Dein Gutschein-Code: <strong>${couponCode}</strong></p>`
          : '',
        couponBlockText: couponEnabled && couponCode ? `Dein Gutschein-Code: ${couponCode}\n\n` : '',
      },
      // SMS vorauswählen, wenn der Vertragspartner Telefon als bevorzugten
      // Kontaktweg hat.
      defaultSelectedChannels: { email: true, sms: Boolean(legacyUpdates.contract_partner.preferred_contact_phone) },
    }];
  }, [
    details?.case?.standort_id,
    details?.case?.wp_user_name,
    customerWpUserId,
    legacyUpdates.contract_partner.first_name,
    legacyUpdates.contract_partner.last_name,
    legacyUpdates.contract_partner.preferred_contact_phone,
    legacyUpdates.student.first_name,
    legacyUpdates.student.last_name,
    teacherSummaryName,
    date,
    startTime,
    lesson.room_name,
    couponEnabled,
    couponCode,
  ]);

  const handleDraftsChange = useCallback((state: { groups: Record<string, GroupDraftState> }) => {
    const nextSerialized = JSON.stringify(state.groups || {});
    setCommunicationDraft((prev) => {
      const prevSerialized = JSON.stringify(prev.groups || {});
      if (prevSerialized === nextSerialized) return prev;
      return { ...prev, groups: state.groups };
    });
  }, []);

  const contractPayload = useMemo<MigrationContractAdjustmentPayload>(() => {
    if (!isContractAdjustment) {
      return {
        person_assignment: {
          student: { mode: 'auto' },
          contract_partner: { mode: 'auto' },
        },
        lesson: {
          teacher_wp_user_id: 0,
          unterrichtsform_id: 0,
          weekday: 1,
          start_time: '',
        },
        start_date: '',
        package_selection: { customer_chooses: true },
        communication: { email_mandatory: true },
      };
    }
    const uformName = details?.target_options?.unterrichtsformen?.find((u) => u.id === lesson.unterrichtsform_id)?.name || lesson.unterrichtsform_name || '';
    const fachName = fachOptions.find((f) => f.id === lesson.fach_id)?.name || lesson.fach_name || '';
    return {
      person_assignment: {
        student: {
          mode: personAssignment.student.mode,
          legacy_person_id: personAssignment.student.legacy_person_id || undefined,
          wp_user_id: personAssignment.student.mode === 'manual' ? personAssignment.student.wp_user_id : undefined,
          draft_profile: personAssignment.student.mode === 'create_new' ? { ...legacyUpdates.student } : undefined,
        },
        contract_partner: {
          mode: personAssignment.contract_partner.mode,
          legacy_person_id: personAssignment.contract_partner.legacy_person_id || undefined,
          wp_user_id: personAssignment.contract_partner.mode === 'manual' ? personAssignment.contract_partner.wp_user_id : undefined,
          draft_profile: personAssignment.contract_partner.mode === 'create_new' ? { ...legacyUpdates.contract_partner } : undefined,
        },
      },
      lesson: {
        teacher_wp_user_id: lesson.teacher_wp_user_id,
        teacher_name: teacherSummaryName || lesson.teacher_name,
        fach_id: lesson.fach_id || undefined,
        fach_name: fachName,
        unterrichtsform_id: lesson.unterrichtsform_id,
        unterrichtsform_name: uformName,
        weekday: lesson.weekday,
        start_time: lesson.start_time,
        room_id: lesson.room_id > 0 ? lesson.room_id : undefined,
        room_name: lesson.room_name || undefined,
        interval_weeks: intervalWeeks,
        duration_min: selectedUnterrichtsformDurationMin,
      },
      start_date: startDate,
      // Bei einer Migration waehlt der Kunde das Folgepaket immer im Kundenbereich –
      // Office-Wizard bestimmt kein vorgewaehltes Paket.
      package_selection: {
        customer_chooses: true,
        selected_package_id: null,
        payment_type: null,
        installments: null,
      },
      communication: {
        email_mandatory: true,
        sms_optional: communicationDraft.sms_optional,
        sms_text: communicationDraft.sms_text,
        groups: communicationDraft.groups as Record<string, unknown> | undefined,
      },
      legacy_updates: legacyUpdates,
      coupon_code: couponEnabled && couponCode ? couponCode : undefined,
      open_appointments: openAppointmentsEnabled
        ? {
            transfer: openAppointmentsResolution === 'credit' || openAppointmentsResolution === 'schedule_before_start'
              ? false
              : openAppointmentsResolution !== 'none_open' && openAppointmentsResolution !== 'skip',
            lehrer_absagen: openLehrerAbsagen,
            aussetzen: openAussetzen,
            ...(openAppointmentsResolution ? { resolution: openAppointmentsResolution } : {}),
          }
        : { transfer: false, lehrer_absagen: 0, aussetzen: 0 },
      history: historyText.trim() ? { description: historyText.trim() } : null,
    };
  }, [
    isContractAdjustment,
    details?.target_options?.unterrichtsformen,
    personAssignment,
    legacyUpdates,
    lesson,
    teacherSummaryName,
    fachOptions,
    startDate,
    communicationDraft,
    intervalWeeks,
    selectedUnterrichtsformDurationMin,
    historyText,
    couponEnabled,
    couponCode,
    openAppointmentsEnabled,
    openLehrerAbsagen,
    openAussetzen,
    openAppointmentsResolution,
  ]);
  const requestPayload: Record<string, unknown> = isContractAdjustment
    ? (contractPayload as unknown as Record<string, unknown>)
    : genericPayload;

  const contractAdjustmentWizardDraft = useMemo<ContractAdjustmentWizardDraft | null>(() => {
    if (!isContractAdjustment) return null;
    const completedSteps = Array.from({ length: Math.max(0, step - 1) }, (_, index) => index + 1);
    return {
      action_type: 'contract_adjustment',
      total_steps: steps.length,
      current_step: step,
      completed_steps: completedSteps,
      wp_user_id: customerWpUserId,
      step1_active_section: step1ActiveSection,
      schedule_view: 'timetable',
      timetable_day_of_week: timetableDayOfWeek,
      start_date_manually_selected: startDateManuallySelected,
      draft_payload: contractPayload,
    };
  }, [
    isContractAdjustment,
    step,
    steps.length,
    customerWpUserId,
    step1ActiveSection,
    timetableDayOfWeek,
    startDateManuallySelected,
    contractPayload,
  ]);

  useEffect(() => {
    if (!open || !caseId || !isContractAdjustment || !contractAdjustmentWizardDraft) return;
    if (!hydrationDoneRef.current) return;
    const serialized = JSON.stringify(contractAdjustmentWizardDraft);
    if (serialized === lastSavedDraftRef.current) return;
    const timer = globalThis.setTimeout(() => {
      void migrationApi
        .saveCaseWizardDraft(caseId, {
          contract_adjustment_wizard: contractAdjustmentWizardDraft,
        })
        .then(() => {
          lastSavedDraftRef.current = serialized;
        })
        .catch(() => undefined);
    }, 500);
    return () => globalThis.clearTimeout(timer);
  }, [open, caseId, isContractAdjustment, contractAdjustmentWizardDraft]);

  const ensureResolvedCustomerProfile = useCallback(async (): Promise<number> => {
    if (!caseId) throw new Error('Case-ID fehlt.');
    const caseEmailExists = Boolean(details?.case.email_exists_in_system);
    const isLinkedOrCreated = Boolean(details?.case.is_linked_or_created || details?.case.person_detail_wp_user_id);
    const manualContractPartnerId = personAssignment.contract_partner.mode === 'manual'
      ? Number(personAssignment.contract_partner.wp_user_id || 0)
      : 0;
    const alreadyResolved = manualContractPartnerId || customerWpUserId;
    if (alreadyResolved > 0) {
      setResolvedCustomerWpUserId(alreadyResolved);
      return alreadyResolved;
    }

    if (caseEmailExists && !isLinkedOrCreated) {
      throw new Error('E-Mail bereits im System: Bitte zuerst Datensatz verknüpfen oder neu anlegen.');
    }

    const draftProfile = legacyUpdates.contract_partner;
    const email = (draftProfile.email || '').trim().toLowerCase();
    if (!email) {
      throw new Error('Für die Anlage eines Kundenprofils wird eine E-Mail im Vertragspartner benötigt.');
    }

    // Der Account-Inhaber ist immer der Vertragspartner. KEIN Fallback auf den
    // Schueler-Namen — bei minderjaehrigen Schuelern wuerde sonst der Account
    // faelschlich auf den Schueler statt auf das Elternteil angelegt.
    const result = await migrationApi.upsertCaseLink(caseId, 'contract_partner', {
      target_type: 'create_new_wp_user',
      mapping_mode: 'auto',
      payload: {
        first_name: draftProfile.first_name || '',
        last_name: draftProfile.last_name || '',
        email,
        phone: (draftProfile.phone || '').trim() || undefined,
        address_street: (draftProfile.address_street || '').trim() || undefined,
        address_zip: (draftProfile.address_zip || '').trim() || undefined,
        address_city: (draftProfile.address_city || '').trim() || undefined,
        birth_date: (draftProfile.birth_date || '').trim() || undefined,
      },
    });
    const resolvedWpUserId = Number(result.resolved_wp_user_id ?? 0);
    if (resolvedWpUserId <= 0) {
      throw new Error('Kundenprofil konnte nicht aufgelöst werden.');
    }
    setResolvedCustomerWpUserId(resolvedWpUserId);
    setPersonAssignment((prev) => ({
      ...prev,
      contract_partner: {
        ...prev.contract_partner,
        mode: 'manual',
        wp_user_id: resolvedWpUserId,
      },
    }));
    return resolvedWpUserId;
  }, [caseId, details?.case.email_exists_in_system, details?.case.is_linked_or_created, details?.case.person_detail_wp_user_id, personAssignment.contract_partner, customerWpUserId, legacyUpdates]);

  const canProceedStep = useMemo(() => {
    if (!isContractAdjustment) return true;
    if (step === 1) {
      const roles: PersonRole[] = ['student', 'contract_partner'];
      const selectedAll = roles.every((role) => personAssignment[role].legacy_person_id > 0);
      const manualAllValid = roles.every((role) => personAssignment[role].mode !== 'manual' || personAssignment[role].wp_user_id > 0);
      return selectedAll && manualAllValid;
    }
    if (step === 2) {
      return lesson.fach_id > 0 && lesson.teacher_id > 0 && lesson.unterrichtsform_id > 0 && !!selectedSlot;
    }
    if (step === 3) return !!roomId;
    if (step === 4) {
      const dateOk = startDate !== '' && !hasTeacherCollision;
      if (!dateOk) return false;
      if (hasOpenAppointmentsDecision && !openAppointmentsResolution) return false;
      return true;
    }
    if (step === 5) return true; // Pakete sind nur informativ – Kunde wählt im Kundenbereich
    if (step === 6) return composerGroups.length > 0;
    return true;
  }, [isContractAdjustment, step, personAssignment, lesson, selectedSlot, roomId, startDate, hasTeacherCollision, composerGroups.length, hasOpenAppointmentsDecision, openAppointmentsResolution]);

  const onNext = async () => {
    if (isContractAdjustment && step === 1) {
      setIsLoading(true);
      setError(null);
      try {
        await ensureResolvedCustomerProfile();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Kundenprofil konnte nicht aufgelöst werden.');
        setIsLoading(false);
        return;
      }
      setIsLoading(false);
    }
    setStep((prev) => Math.min(steps.length, prev + 1));
  };
  const onBack = () => setStep((prev) => Math.max(1, prev - 1));
  const isLastStep = step === steps.length;

  const runPreview = async () => {
    if (!caseId) return;
    setIsLoading(true);
    setError(null);
    try {
      if (isContractAdjustment) {
        await ensureResolvedCustomerProfile();
        if (composerGroups.length === 0) {
          throw new Error('Kommunikation nicht bereit: Bitte zuerst Vertragspartner-Profil verknüpfen oder anlegen.');
        }
      }
      const res = await onPreview(caseId, requestPayload);
      setWarnings(Array.isArray(res.warnings) ? res.warnings : []);
      onNext();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Preview fehlgeschlagen');
    } finally {
      setIsLoading(false);
    }
  };

  const runExecute = async () => {
    if (!caseId) return;
    setIsLoading(true);
    setError(null);
    try {
      await onExecute(caseId, requestPayload);
      if (onDone) await onDone();
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ausführung fehlgeschlagen');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <WizardShell
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={`Schritt ${step} von ${steps.length}: ${steps[step - 1]}`}
      currentStep={step}
      steps={steps.map((label) => ({ label }))}
      onBack={step > 1 ? onBack : undefined}
      canBack={!isLoading}
      onNext={!isLastStep ? (step === steps.length - 1 ? runPreview : onNext) : undefined}
      nextLabel="Weiter"
      canNext={!isLoading && canProceedStep}
      onPrimary={isLastStep ? runExecute : undefined}
      primaryLabel="Ausführen"
      canPrimary={!isLoading}
      isLoading={isLoading}
      contentClassName={isContractAdjustment && step === 2 ? 'w-[98vw] h-[96vh] max-w-[98vw] max-h-[96vh]' : undefined}
      bodyClassName={isContractAdjustment && step === 2 ? 'px-6 overflow-hidden' : undefined}
    >
      <div className={cn(
        isContractAdjustment && step === 2
          ? 'flex h-full min-h-0 flex-col gap-4 py-2'
          : 'space-y-4 py-2'
      )}>
        {isLoadingDetails ? <div className="rounded-md border border-border/70 p-3 text-sm text-muted-foreground">Case-Details werden geladen…</div> : null}
        {error ? <div className="rounded-md border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">{error}</div> : null}
        {warnings.length > 0 ? (
          <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200">
            {warnings.map((w) => <div key={w}>{w}</div>)}
          </div>
        ) : null}

        {!isContractAdjustment && (
          <>
            {step === 1 ? (
              <div className="space-y-2 rounded-md border border-border/70 p-3">
                <Label>Grund</Label>
                <input
                  className="w-full rounded-md border border-input bg-background px-3 py-2"
                  value={String(genericPayload.reason ?? '')}
                  onChange={(e) => setGenericPayload((prev) => ({ ...prev, reason: e.target.value }))}
                />
              </div>
            ) : null}
            {step === 2 ? (
              <div className="space-y-2 rounded-md border border-border/70 p-3">
                <Label>Optionale SMS</Label>
                <textarea
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  rows={4}
                  value={String((genericPayload.communication as Record<string, unknown> | undefined)?.sms_text ?? '')}
                  onChange={(e) =>
                    setGenericPayload((prev) => ({
                      ...prev,
                      communication: {
                        ...(prev.communication as Record<string, unknown>),
                        email_mandatory: true,
                        sms_optional: true,
                        sms_text: e.target.value,
                      },
                    }))
                  }
                />
              </div>
            ) : null}
            {step === 3 ? (
              <div className="rounded-md border border-border/70 p-3 text-sm text-muted-foreground">
                Übersicht bereit. Mit <strong>Ausführen</strong> wird die Aktion gespeichert.
              </div>
            ) : null}
          </>
        )}

        {isContractAdjustment && step === 1 ? (
          <div className="space-y-4">
            {(['student', 'contract_partner'] as PersonRole[]).map((role) => {
              const studentBirthDate = legacyUpdates.student.birth_date;
              const studentAge = calculateAge(studentBirthDate);
              const studentIsAdult = studentAge !== null && studentAge >= 18;
              const ageLabel = formatAgeYearsMonths(legacyUpdates[role].birth_date);
              const faecherForRole = ((details?.legacy_data ?? {}) as Record<string, unknown>)[
                role === 'student' ? 'legacy_student_faecher' : 'legacy_contract_partner_faecher'
              ] as ReadonlyArray<{ id: number; name: string }> | undefined;
              return (
                <PersonAssignmentCard
                  key={role}
                  role={role}
                  legacy={legacyUpdates[role]}
                  onChange={(patch) =>
                    setLegacyUpdates((prev) => ({ ...prev, [role]: { ...prev[role], ...patch } }))
                  }
                  assignment={personAssignment[role]}
                  onAssignmentChange={(patch) => {
                    setPersonAssignment((prev) => ({ ...prev, [role]: { ...prev[role], ...patch } }));
                    if (role === 'contract_partner' && typeof patch.legacy_person_id === 'number') {
                      const nextLegacyId = patch.legacy_person_id;
                      const studentLegacyId = personAssignment.student.legacy_person_id;
                      if (studentIsAdult && nextLegacyId > 0 && nextLegacyId === studentLegacyId) {
                        setLegacyUpdates((prev) => ({
                          ...prev,
                          contract_partner: {
                            ...prev.contract_partner,
                            salutation: prev.student.salutation,
                            first_name: prev.student.first_name,
                            last_name: prev.student.last_name,
                            birth_date: prev.student.birth_date,
                            email: prev.student.email,
                            phone: prev.student.phone,
                            address_street: prev.student.address_street,
                            address_zip: prev.student.address_zip,
                            address_city: prev.student.address_city,
                            address_country: prev.student.address_country,
                            newsletter_opt_in: prev.student.newsletter_opt_in,
                          },
                        }));
                      }
                    }
                  }}
                  relatedPeople={(details?.legacy_data?.legacy_related_people ?? []) as ReadonlyArray<{
                    legacy_person_id: number;
                    name?: string | null;
                    role?: string | null;
                    relation?: string | null;
                  }>}
                  studentLegacyPersonId={personAssignment.student.legacy_person_id}
                  studentName={`${legacyUpdates.student.first_name} ${legacyUpdates.student.last_name}`.trim() || undefined}
                  faecher={faecherForRole}
                  ageLabel={ageLabel}
                  preferredContactEmailLocked={role === 'contract_partner'}
                />
              );
            })}
          </div>
        ) : null}

        {isContractAdjustment && step === 2 ? (
          <div className="flex min-h-0 flex-1 flex-col gap-3">
            <div className="grid shrink-0 gap-3 rounded-md border border-border/70 p-3 md:grid-cols-2">
              <div className="space-y-1 text-sm">
                <Label>Unterrichtsform</Label>
                <Select
                  value={String(lesson.unterrichtsform_id || 0)}
                  onValueChange={(v) => {
                    const nextId = Number(v || 0);
                    const selectedUform = (details?.target_options?.unterrichtsformen ?? []).find((u) => u.id === nextId);
                    setLesson((prev) => ({
                      ...prev,
                      unterrichtsform_id: nextId,
                      unterrichtsform_name: selectedUform?.name ?? '',
                    }));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Bitte wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {(details?.target_options?.unterrichtsformen ?? []).map((u) => (
                      <SelectItem key={u.id} value={String(u.id)}>
                        {u.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1 text-sm">
                <Label>Turnus</Label>
                <Select value={String(intervalWeeks)} onValueChange={(v) => setIntervalWeeks(Number(v))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Wöchentlich</SelectItem>
                    <SelectItem value="2">14-tägig</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex min-h-0 flex-1 flex-col">
              <StepLessonSchedule
              step1ActiveSection={step1ActiveSection}
              step1UnterrichtCollapsed={step1UnterrichtCollapsed}
              step1TerminplanCollapsed={step1TerminplanCollapsed}
              selectedSubjectName={selectedSubjectName}
              teacherSummaryName={teacherSummaryName}
              selectedCourseId={undefined}
              selectedCourse={null}
              fachOptions={fachOptions}
              fachId={lesson.fach_id || undefined}
              teacherOptions={teacherOptions}
              teacherId={lesson.teacher_id || undefined}
              lockTeacher={false}
              teacherAvailability={teacherAvailability}
              selectedSlot={selectedSlot}
              previewEvent={previewEvent}
              timetableDayOfWeek={timetableDayOfWeek}
              timetableReferenceDate={timetableBaseDate}
              canShowTerminplan={canShowTerminplan}
              missingScheduleSelectionText={missingScheduleSelectionText}
              teacherAvailabilityHint={teacherAvailabilityHint}
              showOutsideAvailabilityWarning={false}
              onActivateSection={setStep1ActiveSection}
              onSelectFach={(nextFachId) => {
                const fach = fachOptions.find((f) => f.id === nextFachId);
                setRoomTagsUserModified(false);
                setSelectedRoomTags([]);
                setLesson((prev) => ({
                  ...prev,
                  fach_id: nextFachId,
                  fach_name: fach?.name ?? '',
                  teacher_id: 0,
                  teacher_wp_user_id: 0,
                  teacher_name: '',
                }));
                setSelectedSlot(null);
                setDate(startDate || firstOfNextMonth());
              }}
              onSelectTeacher={(nextTeacherId) => {
                const teacher = teacherOptions.find((t) => t.id === nextTeacherId);
                setLesson((prev) => ({
                  ...prev,
                  teacher_id: nextTeacherId,
                  teacher_wp_user_id: Number(teacher?.wp_user_id ?? 0),
                  teacher_name: teacher ? `${teacher.vorname} ${teacher.nachname}` : '',
                }));
                setSelectedSlot(null);
                setStep1ActiveSection('terminplan');
              }}
              onClearCourse={() => undefined}
              onSelectTimetableSlot={({ dayOfWeek, startTime: slotStart }) => {
                const resolvedEndTime = `${addMinutes(slotStart.slice(0, 5), selectedUnterrichtsformDurationMin)}:00`;
                setSelectedSlot({ dayOfWeek, startTime: slotStart, endTime: resolvedEndTime });
                setTimetableDayOfWeek(dayOfWeek);
                setDate('');
                setStartDateManuallySelected(false);
                setLesson((prev) => ({
                  ...prev,
                  weekday: dayOfWeek,
                  start_time: slotStart.slice(0, 5),
                }));
              }}
              onSelectEvent={() => undefined}
              onSelectTimetableDay={setTimetableDayOfWeek}
            />
            </div>
          </div>
        ) : null}

        {isContractAdjustment && step === 3 ? (
          <StepRoomSelection
            date={roomCandidateDate}
            startTime={startTime}
            endTime={endTime}
            roomUnitsLoading={roomUnitsLoading}
            roomUnitsError={roomUnitsError}
            filteredRoomsForFach={filteredRoomsForFach}
            roomUnitsForDate={roomUnitsForDate}
            roomId={roomId}
            teacherRoomsOnSelectedDay={teacherRoomsOnSelectedDay}
            fachId={lesson.fach_id || undefined}
            fachOptions={fachOptions}
            allRoomTags={allRoomTags}
            selectedRoomTags={selectedRoomTags}
            roomSearchQuery={roomSearchQuery}
            onToggleRoomTag={(tag) => {
              setRoomTagsUserModified(true);
              setSelectedRoomTags((prev) => (prev.includes(tag) ? prev.filter((value) => value !== tag) : [...prev, tag]));
            }}
            onChangeRoomSearchQuery={setRoomSearchQuery}
            onSelectRoom={(id) => setRoomId(id)}
            onOpenRoomPreview={(room) => {
              setRoomPreview(room);
              setRoomPreviewOpen(true);
            }}
            addMinutes={addMinutes}
            overlaps={overlaps}
          />
        ) : null}

        {roomPreviewOpen ? (
          <div className="rounded-md border border-border/70 bg-muted/20 p-3 text-sm">
            <div className="mb-2 font-medium">{roomPreview?.name || 'Raum'}</div>
            <div className="text-muted-foreground">{roomPreview?.address || 'Keine Adresse hinterlegt'}</div>
            <button type="button" className="mt-2 rounded-md border px-3 py-1 text-xs" onClick={() => setRoomPreviewOpen(false)}>
              Schließen
            </button>
          </div>
        ) : null}

        {isContractAdjustment && step === 4 ? (
          <div className="space-y-4">
          <StepDateSelection
            selectedSlot={selectedSlot}
            step2HintText={nextAvailableLoading ? 'Ermittle nächstmöglichen Termin…' : nextAvailableDate ? `Vorschlag: ${nextAvailableDate}` : ''}
            datePageModel={datePageModel}
            date={startDate}
            startTime={startTime}
            teacherDayLoading={teacherDayLoading}
            teacherDayError={teacherDayError}
            hasTeacherCollision={hasTeacherCollision}
            showSelectedSummary={false}
            onPrevPage={() => setDatePage((prev) => prev - 1)}
            onNextPage={() => setDatePage((prev) => prev + 1)}
            onSelectDate={(nextDate, nextStart) => {
              setDate(nextDate);
              setStartDate(nextDate);
              setStartDateManuallySelected(true);
              setLesson((prev) => ({ ...prev, start_time: nextStart }));
            }}
            addMinutes={addMinutes}
          />
          {hasOpenAppointmentsDecision ? (
            <WizardCard>
              <WizardSection title="Offene Termine aus Altvertrag">
                <p className="text-sm text-muted-foreground">
                  {openAppointmentsTotal} Termine sind aus dem alten Vertrag noch offen.
                  Lehrerabsagen: {openLehrerAbsagen} · Aussetzen: {openAussetzen}
                </p>
                <p className="mt-2 text-sm text-muted-foreground">
                  Du kannst die Termine nicht in den neuen Vertrag übertragen. Wie möchtet ihr verbleiben?
                </p>
                <div className="mt-4 space-y-2">
                  {([
                    ['schedule_before_start', 'Die Termine sollen vor dem Vertragsstart nachgeholt werden.'],
                    ['credit', 'Die Termine sollen als Gutschrift gebucht werden. Die Überzahlung muss beim Lehrer ausgeglichen werden.'],
                    ['none_open', 'Es sind keine Termine mehr offen.'],
                    ['skip', 'Ohne Entscheidung fortfahren.'],
                  ] as const).map(([value, label]) => (
                    <label key={value} className="flex cursor-pointer items-start gap-2 rounded-md border border-border/70 p-3 text-sm hover:bg-muted/30">
                      <input
                        type="radio"
                        name="open-appointments-resolution"
                        checked={openAppointmentsResolution === value}
                        onChange={() => setOpenAppointmentsResolution(value)}
                        className="mt-1"
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </WizardSection>
            </WizardCard>
          ) : null}
          </div>
        ) : null}

        {isContractAdjustment && step === 5 ? (
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="rounded-lg bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground">
                Folgepakete
              </div>
              <p className="text-sm text-muted-foreground">
                Nur zur Information — der Kunde wählt sein Paket in der Vertragsanpassung.
              </p>
              {contingentTariffs.length > 0 ? (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {contingentTariffs.map((tariff) => (
                    <div key={tariff.id} className="rounded-md border p-3 text-sm">
                      <div className="font-medium">{tariff.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {CONTINGENT_MODE_LABEL[tariff.contingentMode ?? 'prepaid']}
                      </div>
                      <div className="mt-2 tabular-nums">{tariffPriceLabel(tariff)}</div>
                    </div>
                  ))}
                </div>
              ) : periodicTariff ? (
                <div className="rounded-md border p-3 text-sm">
                  <div className="font-medium">{periodicTariff.name}</div>
                  <div className="mt-1 tabular-nums">{tariffPriceLabel({ ...periodicTariff, installmentCount: null, model: 'periodic' })}</div>
                  <div className="text-xs text-muted-foreground">
                    Beitrag — der Kunde wählt die Zahlweise.
                  </div>
                </div>
              ) : (
                <MigrationPackageList packagePrices={packagePrices} />
              )}
            </div>

            <WizardCard>
              <WizardSection title="Gutscheincode">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm text-muted-foreground">Gutscheincode hinzufügen</span>
                  <Switch checked={couponEnabled} onCheckedChange={setCouponEnabled} />
                </div>
                {couponEnabled ? (
                  <div className="mt-3 grid gap-3 md:grid-cols-2">
                    <div>
                      <Label className="text-xs text-muted-foreground">Betrag (€, einmalig)</Label>
                      <Input
                        type="number"
                        min={0}
                        step={0.01}
                        value={couponAmountEuro}
                        onChange={(e) => setCouponAmountEuro(e.target.value)}
                      />
                    </div>
                    <div className="flex flex-col justify-end gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        disabled={couponGenerating || customerWpUserId <= 0}
                        onClick={() => {
                          const euro = Number(couponAmountEuro.replace(',', '.'));
                          if (!Number.isFinite(euro) || euro <= 0) return;
                          setCouponGenerating(true);
                          void migrationApi
                            .createMigrationCoupon(caseId, {
                              valueCents: Math.round(euro * 100),
                              wpUserId: customerWpUserId,
                            })
                            .then((res) => setCouponCode(res.code))
                            .finally(() => setCouponGenerating(false));
                        }}
                      >
                        {couponGenerating ? 'Generiere…' : 'Code generieren'}
                      </Button>
                      {couponCode ? (
                        <p className="text-sm font-mono font-semibold">{couponCode}</p>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </WizardSection>
            </WizardCard>

          </div>
        ) : null}

        {isContractAdjustment && step === 6 ? (
          <div className="space-y-3 rounded-md border border-border/70 p-3">
            {details?.case?.standort_id && composerGroups.length > 0 ? (
              <CommunicationComposer
                active={open && step === 6}
                standortId={details.case.standort_id}
                groups={composerGroups}
                onDraftsChange={handleDraftsChange}
              />
            ) : (
              <div className="text-sm text-muted-foreground">
                Kommunikation noch nicht bereit. Bitte zuerst in Schritt 1 den Vertragspartner verknüpfen oder als neues Profil anlegen.
              </div>
            )}
          </div>
        ) : null}

        {isContractAdjustment && step === 7 ? (() => {
          // Vertragspartner-Anzeige: zuerst die ggf. editierten Felder, sonst
          // ein Lookup in den Related-People aus dem Snapshot, schliesslich
          // der gespeicherte legacy_contract_partner_name.
          const partnerNameFromForm = `${legacyUpdates.contract_partner.first_name} ${legacyUpdates.contract_partner.last_name}`.trim();
          const partnerLegacyId = personAssignment.contract_partner.legacy_person_id;
          const relatedPeopleArr = ((details?.legacy_data?.legacy_related_people ?? []) as Array<{ legacy_person_id: number; name?: string | null }>);
          const partnerFromRelated = relatedPeopleArr.find((p) => Number(p.legacy_person_id) === Number(partnerLegacyId))?.name ?? '';
          const partnerSnapshotName = String(((details?.legacy_data ?? {}) as Record<string, unknown>).legacy_contract_partner_name ?? '');
          const partnerDisplayName = partnerNameFromForm || partnerFromRelated || partnerSnapshotName || '—';

          // Benachrichtigungen analog Probestunden-Flow: pro Empfänger-Gruppe
          // E-Mail- und SMS-Pills basierend auf den im Composer selektierten
          // Kanälen.
          const channelPillClass = (active: boolean) =>
            cn(
              'inline-flex h-6 items-center rounded-full border px-2.5 text-xs font-medium',
              active
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-muted bg-muted/70 text-muted-foreground',
            );

          return (
          <WizardCard>
            <WizardSection title="Personen">
              <WizardKVGrid>
                <WizardKVItem label="Schüler">{studentDisplayName}</WizardKVItem>
                <WizardKVItem label="Vertragspartner">{partnerDisplayName}</WizardKVItem>
              </WizardKVGrid>
            </WizardSection>
            <WizardSection title="Unterricht">
              <WizardKVGrid>
                <WizardKVItem label="Unterrichtsform">{lesson.unterrichtsform_name || '—'}</WizardKVItem>
                <WizardKVItem label={getSubjectLabels().singular}>{selectedSubjectName}</WizardKVItem>
                <WizardKVItem label="Lehrer">{teacherSummaryName}</WizardKVItem>
                <WizardKVItem label="Wochentag">{weekdayName || '—'}</WizardKVItem>
                <WizardKVItem label="Zeit">{timeRangeLabel}</WizardKVItem>
                <WizardKVItem label="Turnus">{intervalWeeks === 1 ? 'Wöchentlich' : `${intervalWeeks}-tägig`}</WizardKVItem>
                <WizardKVItem label="Raum">{lesson.room_name || '—'}</WizardKVItem>
                <WizardKVItem label="Startdatum">{startDate ? formatDate(startDate) : '—'}</WizardKVItem>
              </WizardKVGrid>
            </WizardSection>
            <WizardSection title="Benachrichtigungen">
              <WizardKVGrid>
                {composerGroups.map((group) => {
                  const draftState = communicationDraft.groups?.[group.key];
                  const emailSelected = draftState?.selectedChannels?.email
                    ?? group.defaultSelectedChannels?.email
                    ?? true;
                  const smsSelected = draftState?.selectedChannels?.sms
                    ?? group.defaultSelectedChannels?.sms
                    ?? false;
                  const recipientName = group.recipients[0]?.name || group.label;
                  return (
                    <WizardKVItem key={group.key} label={group.label}>
                      <div className="flex flex-wrap items-center gap-2">
                        <span>{recipientName}</span>
                        <span className={channelPillClass(emailSelected)}>E-Mail</span>
                        <span className={channelPillClass(smsSelected)}>SMS</span>
                      </div>
                    </WizardKVItem>
                  );
                })}
              </WizardKVGrid>
            </WizardSection>
            <WizardSection title="Event-Historie">
              <div className="grid gap-4">
                <div>
                  <div className="mb-2 text-sm font-medium">Historieneintrag</div>
                  <Textarea
                    rows={5}
                    value={historyText}
                    onChange={(e) => {
                      setHistoryTouched(true);
                      setHistoryText(e.target.value);
                    }}
                    placeholder="Historieneintrag…"
                  />
                  <div className="mt-2 text-xs text-muted-foreground">
                    Wird beim Ausführen als Historieneintrag gespeichert und kann hier angepasst werden.
                  </div>
                </div>
              </div>
            </WizardSection>
          </WizardCard>
          );
        })() : null}
      </div>
    </WizardShell>
  );
}
